import Grid from "../shared/Grid/Grid";
import { GridColumnDefs } from "../../models/Grid/gridColumnDefs";
import { useEffect, useState } from "react";
import { userManagement_LABELS } from "../../constants/labels/UserManagement";
import { updateClinicDetails } from "../../redux/slices/clinicFilterSlice";
import {
  useGetAllClinicNamesQuery,
  useLazyGetAllClinicsQuery,
} from "../../services/rtkQueryServices/clinicService";
import MultiSelectDropdown from "../shared/MultiSelectDropdown/MultiSelectDropdown";
import { Button, Badge } from "@mui/material";
import { ReactComponent as Apply } from "../../assets/svg/Apply.svg";
import { ReactComponent as Reset } from "../../assets/svg/reset.svg";
import { ReactComponent as View } from "../../assets/svg/View.svg";
import { useNavigate } from "react-router-dom";
import { type ClinicInformation } from "../../models/response/clinicInformation";
import { type GetAllClinicsRequest } from "../../models/request/getAllClinicsRequest";
import "./ClinicGrid.scss";
import { Utility } from "../../utils/Utility";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "../../redux/store";

function ClinicsGrid() {
  const [rowData, setRowData] = useState<ClinicInformation[]>([]);
  const [rowcount, setRowCount] = useState<number>(0);
  const filters = useSelector((state: RootState) => state.clinicManagement);
  const [filterCriteria, setFilterCriteria] =
    useState<GetAllClinicsRequest>(filters);
  const searchStringFilter = filterCriteria.searchString;
  const [searchString, setSearchString] = useState<string>(
    searchStringFilter as string
  );
  const [clinicsDropDownList, setClinicsDropDownList] = useState<any>([]);
  const clinicIdFilter = filterCriteria.filters.find(
    (filter: any) => filter.fieldName === "clinicId"
  );
  const [selectedClinicsList, setSelectedClinicsList] = useState<string[]>(
    clinicIdFilter?.values
  );
  const statusIdFilter = filterCriteria.filters.find(
    (filter: any) => filter.fieldName === "isActive"
  );
  const [statusList, setStatusList] = useState<string[]>(
    statusIdFilter?.values
  );
  const [isApplyFiltersEnabled, setIsApplyFiltersEnabled] =
    useState<boolean>(false);
  const [resetPagination, setResetPagination] = useState<boolean>(false);
  const dispatch = useDispatch();

  const [enableClear, setEnableClear] = useState<boolean>(false);
  const {
    isFetching,
    isSuccess,
    isError,
    data: clinicNames,
    refetch,
  } = useGetAllClinicNamesQuery({});
  const [clinicsData] = useLazyGetAllClinicsQuery();
  const navigate = useNavigate();
  useEffect(() => {  
    if (isSuccess && !isFetching) {  
      let resultArray = [];  
      if (clinicNames.data) {  
        resultArray = clinicNames.data.map((item: any) => ({  
          clinicId: item?.clinicId?.toString(),  
          clinicName: item?.clinicName,  
        }));  
      }  
      setClinicsDropDownList(resultArray);  
    }  
  }, [clinicNames]);  
  useEffect(() => {
    clinicsData(filterCriteria).then((res) => {
      if (res.isSuccess && !res.isLoading) {
        setRowData(res?.data?.data?.listOfClinics);
        setRowCount(res?.data?.data?.totalCount);
      }
    });
  }, [filterCriteria]);
  const ClinicContent = (props: any) => {
    return (
      <>
        {props?.data?.clinicName ? (
          <div
            className="text-overflow-ellipse"
            title={props?.data?.clinicName}
          >
            {props?.data?.clinicName}
          </div>
        ) : (
          <div>
            <span
              className="cursor-pointer"
              title="BP Site ID is not validated for this clinic."
            >
              <span className="px-4">—</span>
            </span>
          </div>
        )}
      </>
    );
  };
  const BpSiteIdContent = (props: any) => {
    return (
      <>
        {props?.data?.bpSiteId ? (
          <span>{props?.data?.bpSiteId}</span>
        ) : (
          <span className="font-style-italic alert-info">
            BP Site ID not verified
          </span>
        )}
      </>
    );
  };
  const ClinicStatus = (props: any) => {
    return (
      <div className={"bold " + (props?.data?.isActive ? "green " : "red ")}>
        {" "}
        {props?.data?.isActive ? "Active" : "Inactive"}
      </div>
    );
  };
  const noOfUsers = (props: any) => {
    return (
      <div className="d-flex justify-content-center">
        {props?.data?.assignedGPCount > 0 ? props?.data?.assignedGPCount : 0}
      </div>
    );
  };
  const noOfLicenses = (props: any) => {
    return (
      <div className="d-flex justify-content-center">
        {props?.data?.licenseCount > 0 ? props?.data?.licenseCount : 0}
      </div>
    );
  };
  const createdDate = (props: any) => {
    return Utility.ConvertToDateTime(props?.data?.createdDate);
  };
  const Actions = (props: any) => {
    return (
      <div
        title="View/manage clinic"
        className="cursor-pointer d-flex justify-content-center view-clinic"
        onClick={() => {
          dispatch(updateClinicDetails(filterCriteria));
          navigate(`/clinicDetails/${props?.data?.applicationUserId}`);
        }}
      >
        <b>View</b>
      </div>
    );
  };
  const getAddressbookLastUpdateCss = (props: any) => {
    var badgeTitle = "Failure";
    var badgeColor: "success" | "error" | "warning" = "error";
    switch (props?.data?.addressBookLastUpdateStatus?.toUpperCase()) {
      case "SUCCESS":
        badgeTitle = "Active";
        badgeColor = "success";
        break;
      case "INPROGRESS":
        badgeTitle = "Fetching Status";
        badgeColor = "warning";
        break;
      case "FAILURE":
      default:
        badgeTitle = "Inactive";
        badgeColor = "error";
        break;
    }
    return (
      <div title={badgeTitle} className="d-flex justify-content-center">
        <Badge variant="dot" color={badgeColor}></Badge>
      </div>
    );
  };
  const columnDefs: GridColumnDefs[] = [
    {
      headerName: userManagement_LABELS.bpSiteId,
      field: "bpSiteId",
      cellRenderer: BpSiteIdContent,
      align: "center",
    },
    {
      headerName: userManagement_LABELS.clinic,
      field: "clinicName",
      sortable: true,
      cellRenderer: ClinicContent,
      align: "center",
    },
    {
      headerName: userManagement_LABELS.haloConnection,
      field: "lastAddressBookUpdateStatus",
      cellRenderer: getAddressbookLastUpdateCss,
    },
    {
      headerName: userManagement_LABELS.emailAddress,
      field: "emailAddress",
      sortable: true,
      align: "center",
    },
    {
      headerName: userManagement_LABELS.noOfLicenceKeys,
      field: "licenseCount",
      cellRenderer: noOfLicenses,
    },
    {
      headerName: userManagement_LABELS.noOfUsers,
      field: "assignedGPCount",
      cellRenderer: noOfUsers,
    },
    {
      headerName: userManagement_LABELS.status,
      field: "isActive",
      cellRenderer: ClinicStatus,
      sortable: true,
      align: "center",
    },
    {
      headerName: userManagement_LABELS.createdDate,
      field: "createdDate",
      cellRenderer: createdDate,
      sortable: true,
      align: "center",
    },
    {
      headerName: userManagement_LABELS.actions,
      field: "ACTIONS",
      cellRenderer: Actions,
    },
  ];
  const onPageChange = (value: number) => {
    setFilterCriteria({
      ...filterCriteria,
      pageNumber: value,
    });
  };
  const onPageSizeChange = (value: number) => {
    setFilterCriteria({
      ...filterCriteria,
      pageNumber: 1,
      pageSize: value,
    });
  };
  const searchResults = (value: string) => {
    setSearchString(value);
    setFilterCriteria({
      ...filterCriteria,
      pageNumber: 1,
      searchString: value?.trim().length > 0 ? value.trim() : null,
    });
  };
  const sordOrder = (columnName: string, order: string) => {
    if (order === "asc" || order === "desc") {
      setFilterCriteria({
        ...filterCriteria,
        pageNumber: 1,
        sortByColumnName: columnName,
        sortOrder: order,
      });
      setResetPagination(true);
    } else {
      setFilterCriteria({
        ...filterCriteria,
        pageNumber: 1,
        sortByColumnName: null,
        sortOrder: null,
      });
      setResetPagination(true);
    }
  };
  return (
    <>
      <div className="page-title">Clinic Management</div>
      <div className="clinic-management-wrapper clinic-grid-wrapper d-flex flex-column">
        <Grid
          columnDefs={columnDefs}
          uniqueIdentifierFieldName="id"
          resetPagination={resetPagination}
          rowData={rowData}
          serverSidePagination
          rowCount={rowcount}
          serverSideSorting
          showPagination
          showFilterIcon
          pageSizeList={[10, 20, 50]}
          pageSize={filters.pageSize}
          masterDetail
          isSearch={searchString?.length > 0}
          searchString={searchString}
          showTotalRowCount
          filterTemplate={[
            <>
              <MultiSelectDropdown
                placeholder={"Select Clinic Name"}
                canSearch
                searchFilterDropdownPlaceholder={"Clinic"}
                searchFilterDropdownMenuList={clinicsDropDownList}
                displayPropertyName={"clinicName"}
                valuePropertyName={"clinicId"}
                value={selectedClinicsList || [""]}
                onChange={(val: any) => {
                  setSelectedClinicsList(val);
                  setIsApplyFiltersEnabled(true);
                }}
              />
              <MultiSelectDropdown
                placeholder={"Select Subscription Status"}
                searchFilterDropdownPlaceholder={"Subscription Status"}
                canSearch
                searchFilterDropdownMenuList={[
                  { statusName: "Active", statusId: "1" },
                  { statusName: "Inactive", statusId: "0" },
                ]}
                displayPropertyName={"statusName"}
                valuePropertyName={"statusId"}
                value={statusList || [""]}
                onChange={(val: any) => {
                  setStatusList(val);
                  setIsApplyFiltersEnabled(true);
                }}
              />
              <div className="d-flex pt-2 px-2">
                <Button
                  className={"apply"}
                  disabled={!isApplyFiltersEnabled}
                  onClick={() => {
                    setFilterCriteria({
                      ...filterCriteria,
                      pageNumber: 1,
                      filters: [
                        { fieldName: "clinicId", values: selectedClinicsList },
                        { fieldName: "isActive", values: statusList },
                      ],
                    });
                    setEnableClear(true);
                    setIsApplyFiltersEnabled(false);
                    setResetPagination(true);
                  }}
                >
                  <Apply />
                  <span className="px-2">Apply</span>
                </Button>
              </div>
              <div className="d-flex pt-2 px-2">
                <Button
                  className={"reset"}
                  disabled={!enableClear}
                  onClick={() => {
                    setSelectedClinicsList([]);
                    setStatusList([]);
                    setFilterCriteria({
                      ...filterCriteria,
                      pageNumber: 1,
                      filters: [],
                    });
                    setEnableClear(false);
                    setIsApplyFiltersEnabled(false);
                    setResetPagination(true);
                  }}
                >
                  <Reset />
                  <span className="px-2">Reset</span>
                </Button>
              </div>
            </>,
          ]}
          canSearch
          setSearchQuery={(values: string) => {
            searchResults(values);
            setResetPagination(true);
          }}
          clearSearch={() => {
            if (
              filterCriteria?.searchString &&
              filterCriteria?.searchString?.length >= 3
            ) {
              searchResults("");
              setResetPagination(true);
            }
          }}
          resetSuccess={() => {
            setResetPagination(false);
          }}
          sortBy={(columnName, order) => {
            sordOrder(columnName, order);
          }}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          searchFieldName=""
          defaultSortColumn={filterCriteria.sortByColumnName}
          defaultSortOrder={["desc", "asc", "unsorted"]}
        />
      </div>
    </>
  );
}
export default ClinicsGrid;
